import {
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	type SelectChangeEvent,
} from "@mui/material";
import { type ChangeEvent, useState } from "react";

import CoAppTextField from "@/components/global/components/inputs/CoAppTextField";
import { CoAppActionButton, CoAppCancelTextButton, CoAppCloseIcon, CoAppDialog } from "@/components/global/styled/global.styled";

import { COLLECTION_DATA_TYPE_OPTIONS } from "../collectionDataTypes";

// Match CreateRuleModal select field styling.
const SELECT_FIELD_SX = {
	"& .MuiOutlinedInput-root": {
		borderRadius: "8px",
		background: "#FFFFFF",
		fontSize: 14,
		fontWeight: 500,
		transition: "border-color 0.18s, box-shadow 0.18s",
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "rgba(13, 31, 48, 0.14)",
			borderWidth: 1,
			transition: "border-color 0.18s",
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "rgba(2, 213, 171, 0.5)",
		},
		"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "#02D5AB",
			borderWidth: 1,
		},
		"&.Mui-focused": {
			boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.18)",
		},
		"&.Mui-disabled": {
			background: "rgba(13, 31, 48, 0.03)",
		},
	},
	"& .MuiInputLabel-root": {
		fontSize: 13,
		fontWeight: 500,
		color: "rgba(13, 31, 48, 0.55)",
		"&.Mui-focused": { color: "#057A6D" },
	},
	"& .MuiSelect-select": {
		padding: "10px 12px",
		display: "flex",
		alignItems: "center",
	},
};

const SELECT_MENU_PROPS = {
	slotProps: {
		paper: {
			sx: {
				mt: 0.5,
				borderRadius: 2,
				boxShadow: "0 8px 32px rgba(13, 31, 48, 0.10), 0 1px 3px rgba(13, 31, 48, 0.04)",
			},
		},
	},
};

export interface CreateCollectionFormState {
	name: string;
	platformId: string;
	dataTypeId: number | "";
	nameError: string;
	platformError: string;
	dataTypeError: string;
}

const initialFormState: CreateCollectionFormState = {
	name: "",
	platformId: "",
	dataTypeId: "",
	nameError: "",
	platformError: "",
	dataTypeError: "",
};

interface Platform {
	id: number;
	name: string;
}

interface PlatformOption {
	active: number;
	platform: Platform;
}

interface CreateCollectionModalProps {
	dialogOpen: boolean;
	onClose: () => void;
	onCreate: (input: { name: string; platformId: number; dataTypeId: number }) => Promise<void>;
	isNameTaken: (name: string) => boolean;
	platforms: PlatformOption[];
	platformsLoading: boolean;
}

export default function CreateCollectionModal({
	dialogOpen,
	onClose,
	onCreate,
	isNameTaken,
	platforms,
	platformsLoading,
}: CreateCollectionModalProps) {
	const [form, setForm] = useState<CreateCollectionFormState>(initialFormState);
	const [submitting, setSubmitting] = useState(false);
	const [platformSelectFocused, setPlatformSelectFocused] = useState(false);
	const [platformMenuOpen, setPlatformMenuOpen] = useState(false);
	const [dataTypeSelectFocused, setDataTypeSelectFocused] = useState(false);
	const [dataTypeMenuOpen, setDataTypeMenuOpen] = useState(false);

	const platformHasValue = form.platformId !== "";
	const dataTypeHasValue = form.dataTypeId !== "";

	const resetAndClose = () => {
		setForm(initialFormState);
		setPlatformSelectFocused(false);
		setPlatformMenuOpen(false);
		setDataTypeSelectFocused(false);
		setDataTypeMenuOpen(false);
		onClose();
	};

	const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
		setForm((prev) => ({ ...prev, name: e.target.value, nameError: "" }));
	};

	const handlePlatformChange = (e: SelectChangeEvent<string>) => {
		setForm((prev) => ({ ...prev, platformId: e.target.value, platformError: "" }));
	};

	const handleDataTypeChange = (e: SelectChangeEvent<string>) => {
		const val = e.target.value === "" ? "" : Number(e.target.value);
		setForm((prev) => ({ ...prev, dataTypeId: val, dataTypeError: "" }));
	};

	const handleSubmit = async () => {
		let nameError = "";
		let platformError = "";
		let dataTypeError = "";
		const trimmedName = form.name.trim();

		if (!trimmedName) nameError = "Required *";
		else if (isNameTaken(trimmedName)) nameError = "A collection with this name already exists.";

		if (!form.platformId) platformError = "Required *";
		if (form.dataTypeId === "") dataTypeError = "Required *";

		if (nameError || platformError || dataTypeError) {
			setForm((prev) => ({ ...prev, nameError, platformError, dataTypeError }));
			return;
		}

		setSubmitting(true);
		try {
			await onCreate({
				name: trimmedName,
				platformId: Number(form.platformId),
				dataTypeId: form.dataTypeId as number,
			});
			setForm(initialFormState);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<CoAppDialog
			open={dialogOpen}
			onClose={resetAndClose}
			slotProps={{
				paper: {
					sx: {
						borderRadius: "8px",
						padding: "8px",
						width: "440px",
					},
				},
			}}
		>
			<div>
				<DialogTitle variant="h6">Create collection</DialogTitle>
				<CoAppCloseIcon onClick={resetAndClose} top={10} left={405} />
			</div>
			<DialogContentText
				variant="body2"
				color="text.primary"
				sx={{ marginLeft: "25px", marginRight: "25px" }}
			>
				Define a name, platform, and data type. Entry values must match this data type.
			</DialogContentText>
			<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
				<CoAppTextField
					fullWidth
					label="Collection name"
					value={form.name}
					onChange={handleNameChange}
					required
					error={form.nameError !== ""}
					helperText={form.nameError || "Required *"}
					FormHelperTextProps={{ error: form.nameError !== "" }}
				/>
				<FormControl
					fullWidth
					size="small"
					required
					error={form.platformError !== ""}
					sx={SELECT_FIELD_SX}
				>
					<InputLabel
						id="collection-platform-label"
						shrink={platformHasValue || platformSelectFocused || platformMenuOpen}
					>
						Platform
					</InputLabel>
					<Select
						labelId="collection-platform-label"
						MenuProps={SELECT_MENU_PROPS}
						label="Platform"
						value={form.platformId}
						onChange={handlePlatformChange}
						disabled={platformsLoading}
						onFocus={() => setPlatformSelectFocused(true)}
						onBlur={() => setPlatformSelectFocused(false)}
						onOpen={() => setPlatformMenuOpen(true)}
						onClose={() => setPlatformMenuOpen(false)}
					>
						{platforms.map((p) => (
							<MenuItem key={p.platform.id} value={String(p.platform.id)}>
								{p.platform.name}
							</MenuItem>
						))}
					</Select>
					<FormHelperText error={form.platformError !== ""} sx={{ marginTop: "5px" }}>
						{form.platformError || "Required *"}
					</FormHelperText>
				</FormControl>
				<FormControl
					fullWidth
					size="small"
					required
					error={form.dataTypeError !== ""}
					sx={SELECT_FIELD_SX}
				>
					<InputLabel
						id="collection-datatype-label"
						shrink={dataTypeHasValue || dataTypeSelectFocused || dataTypeMenuOpen}
					>
						Data type
					</InputLabel>
					<Select
						labelId="collection-datatype-label"
						MenuProps={SELECT_MENU_PROPS}
						label="Data type"
						value={form.dataTypeId === "" ? "" : String(form.dataTypeId)}
						onChange={handleDataTypeChange}
						onFocus={() => setDataTypeSelectFocused(true)}
						onBlur={() => setDataTypeSelectFocused(false)}
						onOpen={() => setDataTypeMenuOpen(true)}
						onClose={() => setDataTypeMenuOpen(false)}
					>
						{COLLECTION_DATA_TYPE_OPTIONS.map((opt) => (
							<MenuItem key={opt.id} value={String(opt.id)}>{opt.label}</MenuItem>
						))}
					</Select>
					<FormHelperText error={form.dataTypeError !== ""} sx={{ marginTop: "5px" }}>
						{form.dataTypeError || "Required *"}
					</FormHelperText>
				</FormControl>
			</DialogContent>
			<DialogActions sx={{ marginBottom: "15px", marginRight: "12px" }}>
				<CoAppCancelTextButton onClick={resetAndClose}>Cancel</CoAppCancelTextButton>
				<CoAppActionButton onClick={() => void handleSubmit()} disabled={submitting}>
					Create
				</CoAppActionButton>
			</DialogActions>
		</CoAppDialog>
	);
}
